import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { catalogAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap, Home, User, BarChart3, Users, CreditCard, Settings, LogOut, Menu,
  ShoppingCart, Plus, Minus, Trash2, Package, UserPlus, Calendar,
  Wifi, QrCode, ArrowRight, ShoppingBag, ClipboardList
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ShopPage = ({ isDashboard = false }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('signa_cart') || '[]'); }
    catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await catalogAPI.list();
        setCatalog(res.data);
      } catch (error) { toast.error('Failed to load shop'); }
      finally { setLoading(false); }
    };
    fetchCatalog();
  }, []);

  useEffect(() => {
    localStorage.setItem('signa_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.item_id === item.item_id);
      if (existing) return prev.map(c => c.item_id === item.item_id ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { item_id: item.item_id, name: item.name, price: item.price, quantity: 1, artifact_type: item.artifact_type }];
    });
    toast.success(`${item.name} added to cart`);
  };

  const updateQuantity = (itemId, delta) => {
    setCart(prev => prev.map(c => {
      if (c.item_id !== itemId) return c;
      const newQty = c.quantity + delta;
      return newQty > 0 ? { ...c, quantity: newQty } : c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => prev.filter(c => c.item_id !== itemId));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.quantity, 0);

  const handleCheckout = () => {
    if (!isAuthenticated) {
      toast.info('Please log in to place an order');
      navigate('/login');
      return;
    }
    navigate('/shop/checkout');
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Dashboard sidebar nav
  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/dashboard' },
    { icon: <User className="w-5 h-5" />, label: 'Profiles', path: '/dashboard' },
    { icon: <CreditCard className="w-5 h-5" />, label: 'Artifacts', path: '/artifacts' },
    { icon: <Calendar className="w-5 h-5" />, label: 'Scheduling', path: '/scheduling' },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Customers', path: '/customers' },
    { icon: <Users className="w-5 h-5" />, label: 'Leads', path: '/leads' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop', path: '/dashboard/shop' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/dashboard/orders' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];
  if (user?.role === 'super_admin') {
    navItems.splice(9, 0, { icon: <Zap className="w-5 h-5" />, label: 'Admin', path: '/admin' });
  }

  // Separate starter kit and individual artifacts
  const starterKit = catalog.find(i => i.is_starter_kit);
  const artifacts = catalog.filter(i => !i.is_starter_kit);

  const shopContent = (
    <>
      {/* Starter Kit Hero */}
      {starterKit && (
        <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-900 rounded-2xl p-8 mb-8 overflow-hidden" data-testid="starter-kit-hero">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_60%)]" />
          <div className="relative flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-300 mb-2 block">Best Value</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {starterKit.name}
              </h2>
              <p className="text-indigo-200 mb-4 max-w-md leading-relaxed">{starterKit.description}</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-bold text-white">₱{starterKit.price.toLocaleString()}</span>
                <span className="text-indigo-300 text-sm">one-time</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {['2 Smart Artifacts', '1 Profile', 'Lead Capture', 'Basic Analytics', '1 Video', '1 PDF'].map(f => (
                  <span key={f} className="text-xs px-3 py-1 bg-white/10 text-white rounded-full">{f}</span>
                ))}
              </div>
              <Button
                onClick={() => addToCart(starterKit)}
                className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold px-8 py-3 text-base"
                data-testid="add-starter-kit-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" /> Add to Cart
              </Button>
            </div>
            <div className="w-48 h-48 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10">
              <ShoppingBag className="w-20 h-20 text-white/40" />
            </div>
          </div>
        </div>
      )}

      {/* Artifact Grid */}
      <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Smart Networking Artifacts
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {artifacts.map(item => (
          <div key={item.item_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group" data-testid={`shop-item-${item.item_id}`}>
            <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center relative">
              {item.image_url ? (
                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
              ) : (
                <Package className="w-16 h-16 text-slate-300 group-hover:text-indigo-300 transition-colors" />
              )}
              <div className="absolute top-3 right-3 flex gap-1">
                {item.nfc_supported && (
                  <span className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded-full flex items-center gap-1">
                    <Wifi className="w-3 h-3" /> NFC
                  </span>
                )}
                {item.qr_supported && (
                  <span className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded-full flex items-center gap-1">
                    <QrCode className="w-3 h-3" /> QR
                  </span>
                )}
              </div>
            </div>
            <div className="p-5">
              <h3 className="text-base font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.name}</h3>
              <p className="text-sm text-slate-500 mb-3 line-clamp-2">{item.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xl font-bold text-slate-900">₱{item.price.toLocaleString()}</span>
                <Button onClick={() => addToCart(item)} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`add-to-cart-${item.item_id}`}>
                  <ShoppingCart className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pricing Plans */}
      <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Subscription Plans
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {/* Starter */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Starter Kit</h3>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-3xl font-bold text-slate-900">₱1,299</span>
            <span className="text-sm text-slate-500">one-time</span>
          </div>
          <ul className="space-y-2 text-sm text-slate-600 mb-6">
            {['2 Smart Artifacts', '1 Profile', 'Lead capture', 'Basic analytics', '1 video upload', '1 PDF upload', 'Signa hosted profile'].map(f => (
              <li key={f} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />{f}</li>
            ))}
          </ul>
          <Button variant="outline" className="w-full" onClick={() => starterKit && addToCart(starterKit)} data-testid="plan-starter-btn">Get Started</Button>
        </div>
        {/* Pro */}
        <div className="bg-indigo-600 rounded-xl p-6 text-white relative">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold uppercase bg-amber-400 text-amber-900 px-3 py-1 rounded-full">Most Popular</span>
          <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Pro</h3>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-3xl font-bold">₱500</span>
            <span className="text-sm text-indigo-200">per user/month</span>
          </div>
          <ul className="space-y-2 text-sm text-indigo-100 mb-6">
            {['6 color profiles', 'Unlimited artifacts', 'Full workflow builder', 'Campaign artifacts', 'Profile scheduling', 'Full analytics suite', 'Lead export', 'Custom domain', 'Remove Signa branding'].map(f => (
              <li key={f} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-white rounded-full" />{f}</li>
            ))}
          </ul>
          <Button className="w-full bg-white text-indigo-700 hover:bg-indigo-50 font-semibold" data-testid="plan-pro-btn">Subscribe</Button>
        </div>
        {/* Enterprise */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Enterprise</h3>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-3xl font-bold text-slate-900">Custom</span>
          </div>
          <ul className="space-y-2 text-sm text-slate-600 mb-6">
            {['Everything in Pro', 'White-label deployment', 'API access', 'SLA & priority support', 'Bulk artifact provisioning', 'Organization analytics', 'Advanced branding'].map(f => (
              <li key={f} className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />{f}</li>
            ))}
          </ul>
          <Button variant="outline" className="w-full" data-testid="plan-enterprise-btn">Contact Sales</Button>
        </div>
      </div>
    </>
  );

  // ============ PUBLIC LAYOUT ============
  if (!isDashboard) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="shop-page">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
            </Link>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowCart(!showCart)} className="relative p-2 text-slate-600 hover:text-slate-900" data-testid="cart-toggle-btn">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 text-white text-xs rounded-full flex items-center justify-center font-bold">{cartCount}</span>
                )}
              </button>
              {isAuthenticated ? (
                <Link to="/dashboard" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">Dashboard</Link>
              ) : (
                <Link to="/login"><Button className="bg-indigo-600 hover:bg-indigo-700" size="sm">Sign In</Button></Link>
              )}
            </div>
          </div>
        </header>

        {/* Cart Drawer */}
        {showCart && <CartDrawer cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} cartTotal={cartTotal} onCheckout={handleCheckout} onClose={() => setShowCart(false)} />}

        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa Shop</h1>
            <p className="text-slate-500">Smart networking artifacts for the modern professional</p>
          </div>
          {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : shopContent}
        </main>
      </div>
    );
  }

  // ============ DASHBOARD LAYOUT ============
  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-shop-page">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, i) => (
              <li key={i}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {item.icon}<span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />Sign out
          </Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Shop</h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <button onClick={() => setShowCart(!showCart)} className="relative p-2 text-slate-600 hover:text-slate-900" data-testid="cart-toggle-btn">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 text-white text-xs rounded-full flex items-center justify-center font-bold">{cartCount}</span>
                )}
              </button>
            </div>
          </div>
        </header>

        {showCart && <CartDrawer cart={cart} updateQuantity={updateQuantity} removeFromCart={removeFromCart} cartTotal={cartTotal} onCheckout={handleCheckout} onClose={() => setShowCart(false)} />}

        <main className="p-6" data-testid="shop-content">
          {loading ? <div className="flex justify-center py-12"><div className="spinner" /></div> : shopContent}
        </main>
      </div>
    </div>
  );
};

// ============ CART DRAWER ============
const CartDrawer = ({ cart, updateQuantity, removeFromCart, cartTotal, onCheckout, onClose }) => (
  <div className="fixed inset-0 z-50" onClick={onClose}>
    <div className="absolute inset-0 bg-black/30" />
    <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl" onClick={e => e.stopPropagation()} data-testid="cart-drawer">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Cart</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(item => (
                <div key={item.item_id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3" data-testid={`cart-item-${item.item_id}`}>
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                    <Package className="w-6 h-6 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                    <p className="text-sm text-slate-500">₱{item.price.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQuantity(item.item_id, -1)} className="w-7 h-7 flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-600" data-testid={`qty-minus-${item.item_id}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center text-sm font-medium" data-testid={`qty-${item.item_id}`}>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.item_id, 1)} className="w-7 h-7 flex items-center justify-center rounded bg-slate-200 hover:bg-slate-300 text-slate-600" data-testid={`qty-plus-${item.item_id}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button onClick={() => removeFromCart(item.item_id)} className="text-slate-400 hover:text-red-500" data-testid={`remove-${item.item_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {cart.length > 0 && (
          <div className="border-t border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-slate-600">Subtotal</span>
              <span className="text-xl font-bold text-slate-900" data-testid="cart-total">₱{cartTotal.toLocaleString()}</span>
            </div>
            <Button onClick={onCheckout} className="w-full bg-indigo-600 hover:bg-indigo-700 py-3 text-base font-semibold" data-testid="checkout-btn">
              Checkout <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default ShopPage;
